import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import {
  Gift,
  Users,
  Heart,
  Share2,
  Check,
  ArrowRight,
  Church,
  Baby,
  Cake,
  TreePine,
  HomeIcon,
  GraduationCap,
  Sparkles,
} from 'lucide-react';
import Button from 'shared/components/ui/Button';
import { Card } from 'shared/components/ui/Card';
import PublicWrapper from 'shared/components/layout/PublicWrapper';
import { URLS } from 'configs';
import './SharedWishlistsPage.scss';

const SharedWishlistsPage = () => {
  const { t } = useTranslation();

  const recipientFeatures = [
    {
      icon: Share2,
      title: t('wishlist_recipient1_title', 'Share Easily'),
      desc: t('wishlist_recipient1_desc', 'One link to share with everyone'),
    },
    {
      icon: Check,
      title: t('wishlist_recipient2_title', 'No Duplicates'),
      desc: t('wishlist_recipient2_desc', 'Items marked as purchased automatically'),
    },
    {
      icon: Heart,
      title: t('wishlist_recipient3_title', 'Add Priority Levels'),
      desc: t('wishlist_recipient3_desc', 'Must-have vs nice-to-have'),
    },
    {
      icon: Users,
      title: t('wishlist_recipient4_title', 'Add Notes'),
      desc: t('wishlist_recipient4_desc', 'Size, color, variant preferences'),
    },
  ];

  const giverBenefits = [
    {
      title: t('wishlist_giver1_title', 'See What They Want'),
      desc: t('wishlist_giver1_desc', 'No more guessing games. Browse their actual wishlist.'),
    },
    {
      title: t('wishlist_giver2_title', 'Mark as Purchased'),
      desc: t('wishlist_giver2_desc', 'Hidden from recipient to keep the surprise'),
    },
    {
      title: t('wishlist_giver3_title', 'Coordinate with Others'),
      desc: t('wishlist_giver3_desc', 'See what others are buying to avoid duplicates'),
    },
    {
      title: t('wishlist_giver4_title', 'Filter by Price'),
      desc: t('wishlist_giver4_desc', 'Find gifts within your budget instantly'),
    },
  ];

  const occasions = [
    {
      icon: Church,
      title: t('occasion1_title', 'Wedding Registries'),
      desc: t('occasion1_desc', 'Complete registry for your special day'),
    },
    {
      icon: Baby,
      title: t('occasion2_title', 'Baby Showers'),
      desc: t('occasion2_desc', 'Everything for the new arrival'),
    },
    {
      icon: Cake,
      title: t('occasion3_title', 'Birthday Wishlists'),
      desc: t('occasion3_desc', "Gifts you'll actually use and love"),
    },
    {
      icon: TreePine,
      title: t('occasion4_title', 'Holiday Gift Guides'),
      desc: t('occasion4_desc', 'Make holiday shopping stress-free'),
    },
    {
      icon: HomeIcon,
      title: t('occasion5_title', 'Housewarming'),
      desc: t('occasion5_desc', 'Build your new home together'),
    },
    {
      icon: GraduationCap,
      title: t('occasion6_title', 'Graduation'),
      desc: t('occasion6_desc', 'Celebrate achievements meaningfully'),
    },
  ];

  const socialFeatures = [
    {
      title: t('social1_title', 'Follow Friends'),
      desc: t('social1_desc', 'See their public wishlists for inspiration'),
    },
    {
      title: t('social2_title', 'Community Polls'),
      desc: t('social2_desc', '"Help me choose!" - Get feedback from friends'),
    },
    {
      title: t('social3_title', 'Share on Social'),
      desc: t('social3_desc', 'Post your wishlist to Instagram, Facebook, Twitter'),
    },
  ];

  return (
    <PublicWrapper>
      <Helmet>
        <title>Shared Wishlists for Gifts | Perfect Gift Registry | Likiee</title>
        <meta
          name='description'
          content='Create shareable wishlists for birthdays, weddings, holidays. Friends and family can see what you want, mark items as purchased, and coordinate gifts. No more guessing, no duplicate gifts.'
        />
        <meta
          name='keywords'
          content='shared wishlist, gift registry, wedding registry, birthday wishlist, gift coordination, holiday gift list, baby shower registry'
        />
      </Helmet>
      <main className='shared-wishlists-page'>
        {/* Hero Section */}
        <section className='shared-wishlists-page__hero'>
          <div className='shared-wishlists-page__decorative-circle shared-wishlists-page__decorative-circle--1' />
          <div className='shared-wishlists-page__decorative-circle shared-wishlists-page__decorative-circle--2' />
          <div className='shared-wishlists-page__decorative-circle shared-wishlists-page__decorative-circle--3' />

          <div className='container'>
            <div className='shared-wishlists-page__hero-content'>
              <div className='shared-wishlists-page__badge'>
                <Sparkles className='shared-wishlists-page__badge-icon' />
                <span>{t('wishlist_badge', 'Smart Gift Registry')}</span>
              </div>

              <h1 className='shared-wishlists-page__hero-title'>
                {t('wishlist_hero_title_1', "Gifts They'll")}
                <span className='gradient-text'>{t('wishlist_hero_title_2', ' Actually Love')}</span>
                <br />
                {t('wishlist_hero_title_3', 'Shared & Simplified')}
              </h1>
              <p className='shared-wishlists-page__hero-description'>
                {t(
                  'wishlist_hero_description',
                  'Create wishlists that friends and family can access and contribute to. Get exactly what you want, and give gifts that truly matter.',
                )}
              </p>

              {/* Stats Showcase */}
              <div className='shared-wishlists-page__stats'>
                <div className='shared-wishlists-page__stat'>
                  <div className='shared-wishlists-page__stat-value'>{t('wishlist_stat_users', '100K+')}</div>
                  <div className='shared-wishlists-page__stat-label'>
                    {t('wishlist_stat_users_label', 'Active Users')}
                  </div>
                </div>
                <div className='shared-wishlists-page__stat'>
                  <div className='shared-wishlists-page__stat-value'>{t('wishlist_stat_wishlists', '500K+')}</div>
                  <div className='shared-wishlists-page__stat-label'>
                    {t('wishlist_stat_wishlists_label', 'Wishlists Created')}
                  </div>
                </div>
                <div className='shared-wishlists-page__stat'>
                  <div className='shared-wishlists-page__stat-value'>{t('wishlist_stat_satisfaction', '99%')}</div>
                  <div className='shared-wishlists-page__stat-label'>
                    {t('wishlist_stat_satisfaction_label', 'Gift Satisfaction')}
                  </div>
                </div>
              </div>

              <div className='shared-wishlists-page__hero-actions'>
                <a href={URLS.CONTACT} className='shared-wishlists-page__cta-link'>
                  <Button className='button-hero'>
                    <Gift />
                    {t('contact_us', 'Contact us')}
                    <ArrowRight />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* For Gift Recipients */}
        <section className='shared-wishlists-page__recipients'>
          <div className='container'>
            <div className='shared-wishlists-page__recipients-header'>
              <Gift />
              <h2>{t('wishlist_for_recipients', 'For Gift Recipients')}</h2>
              <p>{t('wishlist_recipients_description', 'Create the perfect wishlist for any occasion')}</p>
            </div>

            <div className='shared-wishlists-page__recipients-grid'>
              {recipientFeatures.map(feature => (
                <Card key={feature.title} className='shared-wishlists-page__recipient-feature'>
                  <div className='shared-wishlists-page__feature-icon'>
                    <feature.icon />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* For Gift Givers */}
        <section className='shared-wishlists-page__givers'>
          <div className='container'>
            <div className='shared-wishlists-page__givers-header'>
              <Users />
              <h2>{t('wishlist_for_givers', 'For Gift Givers')}</h2>
              <p>{t('wishlist_givers_description', 'Give gifts they actually want')}</p>
            </div>

            <div className='shared-wishlists-page__givers-grid'>
              {giverBenefits.map(benefit => (
                <Card key={benefit.title} className='shared-wishlists-page__giver-benefit'>
                  <Check />
                  <div className='shared-wishlists-page__benefit-content'>
                    <h3>{benefit.title}</h3>
                    <p>{benefit.desc}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Special Occasions */}
        <section className='shared-wishlists-page__occasions'>
          <div className='container'>
            <h2 className='shared-wishlists-page__section-title'>
              {t('wishlist_occasions_title', 'Perfect for Every Occasion')}
            </h2>

            <div className='shared-wishlists-page__occasions-grid'>
              {occasions.map(occasion => (
                <Card key={occasion.title} className='shared-wishlists-page__occasion'>
                  <div className='shared-wishlists-page__occasion-icon'>
                    <occasion.icon />
                  </div>
                  <h3>{occasion.title}</h3>
                  <p>{occasion.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Social Features */}
        <section className='shared-wishlists-page__social'>
          <div className='container'>
            <h2 className='shared-wishlists-page__section-title'>{t('wishlist_social_title', 'Social Features')}</h2>

            <div className='shared-wishlists-page__social-grid'>
              {socialFeatures.map(feature => (
                <Card key={feature.title} className='shared-wishlists-page__social-feature'>
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className='shared-wishlists-page__cta'>
          <div className='container'>
            <div className='shared-wishlists-page__cta-content'>
              <h2>{t('wishlist_cta_title', 'Ready to Create Your First Wishlist?')}</h2>
              <p>{t('wishlist_cta_description', 'Join thousands celebrating special moments with meaningful gifts')}</p>
              <a href={URLS.CONTACT} className='shared-wishlists-page__cta-link'>
                <Button className='button-secondary'>
                  <Gift />
                  {t('contact_us', 'Contact us')}
                  <ArrowRight />
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
    </PublicWrapper>
  );
};

export default SharedWishlistsPage;
